import { useState, useEffect, useRef } from "react";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const VideoBackground = () => {
  const videos = ["Z7Qnr7Mje2A", "kl0fa0jaGpY", "2YB7YUeiTeg"];

  // Get a random video index on component mount
  const getRandomIndex = () => Math.floor(Math.random() * videos.length);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(() =>
    getRandomIndex(),
  );
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to initialize YouTube player
  const initializePlayer = () => {
    if (!window.YT || !window.YT.Player) {
      setTimeout(initializePlayer, 100);
      return;
    }

    if (playerRef.current) {
      return;
    }

    // Get the protocol and host from the current window location
    const protocol = window.location.protocol;
    const host = window.location.host;
    const origin = `${protocol}//${host}`;

    try {
      new window.YT.Player(
        `youtube-player-${currentVideoIndex}`,
        {
          height: "50%",
          width: "50%",
          videoId: videos[currentVideoIndex],
          playerVars: {
            autoplay: 1,
            controls: 0,
            showinfo: 0,
            modestbranding: 1,
            loop: 1,
            playlist: videos[currentVideoIndex],
            mute: 1,
            iv_load_policy: 3,
            rel: 0,
            enablejsapi: 1,
            origin: origin,
            widget_referrer: origin,
            host: "https://www.youtube-nocookie.com",
          },
          events: {
            onReady: handlePlayerReady,
            onStateChange: handlePlayerStateChange,
            onError: handleError,
          },
        },
      );
    } catch (error) {
      console.error("Error initializing player:", error);
    }
  };

  const handlePlayerReady = (event: { target: any }) => {
    playerRef.current = event.target;
    setIsPlayerReady(true);
    event.target.playVideo();
    // Always keep video muted
    event.target.mute();
  };

  const handlePlayerStateChange = (event: { data: number; target: any }) => {
    if (event.data === window.YT.PlayerState.ENDED) {
      handleNextVideo();
    } else if (event.data === window.YT.PlayerState.PLAYING) {
      // Always ensure video is muted
      event.target.mute();
    }
  };

  const handleError = (event: any) => {
    console.error("YouTube Player Error:", event.data);
    setIsPlayerReady(false);
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
        playerRef.current = null;
      } catch (e) {
        console.error("Error destroying player:", e);
      }
    }
    // Wait a bit before trying to reinitialize
    setTimeout(initializePlayer, 1000);
  };

  const handleNextVideo = () => {
    if (!isPlayerReady || !playerRef.current) {
      return;
    }

    // Get a random index that's different from the current one
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * videos.length);
    } while (newIndex === currentVideoIndex && videos.length > 1);

    setCurrentVideoIndex(newIndex);

    try {
      playerRef.current.loadVideoById({
        videoId: videos[newIndex],
        startSeconds: 0,
      });

      console.log(`Video ended. Loading new random video: ${videos[newIndex]}`);

      // Always ensure video is muted
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.mute();
        }
      }, 500);
    } catch (error) {
      console.error("Error changing video:", error);
    }
  };

  const loadYouTubeAPI = () => {
    if (!document.getElementById("youtube-api")) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.id = "youtube-api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  };

  useEffect(() => {
    loadYouTubeAPI();

    if (typeof window !== "undefined") {
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) {
      return;
    }
    // Any code that depends on the player being ready goes here
  }, [currentVideoIndex, isPlayerReady]);

  return (
    <div
      className="fixed top-0 left-0 w-full h-full overflow-hidden z-0"
      ref={containerRef}
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70 z-10" />

      {/* Video background */}
      <div className="relative w-full h-full bg-black">
        <div
          id={`youtube-player-${currentVideoIndex}`}
          className="w-full h-full"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(1.5)",
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
};

export default VideoBackground;
